import React, { useEffect, useState } from 'react'


import { Badge, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, number, bool, func, object,
} from 'prop-types'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const averageTagWidth = 40
const averagePriceTagWidth = 65
const averageDiscountTagWidth = 42
const plusSomethingTagWidth = 30
const extraSpace = 20
const defaultMaxTagsCount = 4

function getMaxTagsCount(cardWidth, isDiscountOrRibbonTagsActive) {
  if (!cardWidth) {
    return defaultMaxTagsCount
  }

  let availableWidth = cardWidth - averagePriceTagWidth - plusSomethingTagWidth - extraSpace
  if (isDiscountOrRibbonTagsActive) {
    availableWidth -= averageDiscountTagWidth
  }

  let tagsToShow = 0
  while (availableWidth > 0) {
    availableWidth -= averageTagWidth
    tagsToShow += 1
  }

  return tagsToShow
}

function ModelCardFormatTags({
  tags = EMPTY_ARRAY,
  tagsCount = 0,
  currentSelectedFileTypes,
  setSelectedFilters,
  trackFilterChange,
  isVisible,
  isDiscountOrRibbonTagsActive,
  articleRef,
}) {
  const [cardWidth, setCardWidth] = useState(0)

  // Observe the card, don't listen on window. A results grid mounts up to 120 of these, so one
  // `resize` listener each meant 120 synchronous clientWidth reads and 120 setState calls per
  // resize event - enough to trip React's nested-update guard ("Maximum update depth exceeded")
  // during a continuous drag. The browser coalesces observer callbacks to one per frame, and the
  // observer also catches width changes the window never sees (card size setting, sidebar toggles).
  useEffect(() => {
    const article = articleRef.current
    if (!article) {
      return undefined
    }

    if (typeof ResizeObserver === 'undefined') {
      setCardWidth(article.clientWidth)
      return undefined
    }

    // The observer reports the first size too, after layout. Reading clientWidth here instead
    // forced a synchronous reflow per card at hydration. The article has no padding, so its
    // content box width is its clientWidth.
    const observer = new ResizeObserver((entries) => {
      setCardWidth(Math.round(entries[entries.length - 1].contentRect.width))
    })
    observer.observe(article)

    return () => observer.disconnect()
  }, [articleRef])

  // Derived rather than effect-driven state: the count is a pure function of the measured width, and
  // holding it in state cost a second commit per card per resize on top of the measurement itself.
  const maxTagsCount = getMaxTagsCount(cardWidth, isDiscountOrRibbonTagsActive)

  function triggerFiltersChange(isCurrentlySelected, id, selectedLabel) {
    if (isCurrentlySelected) {
      setSelectedFilters({
        name: 'fileTypes',
        value: currentSelectedFileTypes.filter((item) => item !== `${id}`),
      })
    } else {
      trackFilterChange({
        name: 'fileTypes',
        value: selectedLabel,
        eventSource: 'model card',
      })
      setSelectedFilters({
        name: 'fileTypes',
        value: [...currentSelectedFileTypes, `${id}`],
      })
    }
  }

  const badges = tags
    .filter(({ name }) => !!name)
    .map(({ name, id }) => {
      const isSelected = currentSelectedFileTypes.includes(`${id}`)
      const selectedLabel = name.replace(/\./g, '')

      return ({
        isSelected,
        type: isSelected ? 'primary' : 'white',
        hasBorder: !isSelected,
        value: selectedLabel,
        onClick: () => triggerFiltersChange(isSelected, id, selectedLabel),
      })
    })
    .sort((a, b) => b.isSelected - a.isSelected)
    .slice(0, maxTagsCount)

  const moreItemsCount = tagsCount > maxTagsCount ? {
    type: 'white',
    hasBorder: true,
    value: `+${tagsCount - maxTagsCount}`,
    className: 'card-3d-model__format-tags__more-items',
  } : null

  const temp = [...badges, moreItemsCount].filter(Boolean)

  return (
    <div className={classnames(
      'card-3d-model__format-tags',
      isVisible && 'card-3d-model__format-tags--visible',
    )}
    >
      {temp.map(({
        type, value, hasBorder, onClick, className,
      }) => (
        <Badge type={type} onClick={onClick} hasBorder={hasBorder} key={value} className={className}>
          {value}
        </Badge>
      ))}
    </div>
  )
}

ModelCardFormatTags.propTypes = {
  tags: array,
  tagsCount: number,
  currentSelectedFileTypes: array.isRequired,
  setSelectedFilters: func.isRequired,
  trackFilterChange: func.isRequired,
  isVisible: bool.isRequired,
  isDiscountOrRibbonTagsActive: bool.isRequired,
  articleRef: object.isRequired,
}

export default ModelCardFormatTags
